import {
  APIErrorTypes,
  FieldErrorType,
  InvalidFieldDetailsError,
} from "pages/register/types/apiError";
import {
  InlineError,
  TextInputField,
} from "pages/register/types/userRegistrationForm";

const basicInfoErrorTypes = [
  FieldErrorType.FirstNameValidationError,
  FieldErrorType.LastNameValidationError,
  FieldErrorType.EmailValidationError,
];

const loginInfoErrorTypes = [
  FieldErrorType.UsernameValidationError,
  FieldErrorType.UsernameTakenError,
  FieldErrorType.PasswordValidationError,
  FieldErrorType.CommonPasswordValidationError,
];

const errorTypeToInlineError: Partial<Record<FieldErrorType, TextInputField>> =
  {
    [FieldErrorType.FirstNameValidationError]: TextInputField.FirstName,
    [FieldErrorType.LastNameValidationError]: TextInputField.LastName,
    [FieldErrorType.UsernameValidationError]: TextInputField.Username,
    [FieldErrorType.UsernameTakenError]: TextInputField.Username,
    [FieldErrorType.EmailValidationError]: TextInputField.Email,
    [FieldErrorType.PasswordValidationError]: TextInputField.Password,
  };

const genericErrorMessage = "Something went wrong. Please try again.";

export type ErrorFlags = {
  hasBasicInfoFormError: boolean;
  hasLoginFormError: boolean;
  hasErrorsOnBothForms: boolean;
  hasCommonPasswordError: boolean;
  hasUnrecoverableError: boolean;
};

export type CreateUserError = {
  inlineError: InlineError;
  errorFlags: ErrorFlags;
};

const isServerError = (
  errorResponse: InvalidFieldDetailsError<APIErrorTypes>,
): boolean => {
  return errorResponse.type === APIErrorTypes.OktaServerError;
};

const parseCreateUserError = (
  errorResponse: InvalidFieldDetailsError<APIErrorTypes.FormValidationError>,
): CreateUserError => {
  const invalidFields = errorResponse.invalidFields || [];
  const inlineErrorMap: InlineError = {};

  let hasBasicInfoFormError = false;
  let hasLoginFormError = false;

  invalidFields.forEach((invalidField) => {
    const errorType = invalidField.type;
    const inlineError = errorTypeToInlineError[errorType];

    if (!inlineError) return;

    const errorMessage =
      errorType === FieldErrorType.UsernameTakenError
        ? "Someone already grabbed that username."
        : // TODO: Logic for other error messages
          genericErrorMessage;

    inlineErrorMap[inlineError] = errorMessage;

    if (basicInfoErrorTypes.includes(errorType)) {
      hasBasicInfoFormError = true;
    } else if (loginInfoErrorTypes.includes(errorType)) {
      hasLoginFormError = true;
    }
  });

  const hasErrorsOnBothForms = hasBasicInfoFormError && hasLoginFormError;

  return {
    // If errors exist on both forms, the inline errors will be replaced with a page level error
    inlineError: hasErrorsOnBothForms ? {} : inlineErrorMap,
    errorFlags: {
      hasErrorsOnBothForms,
      hasBasicInfoFormError,
      hasLoginFormError,
      hasUnrecoverableError: isServerError(errorResponse),
      hasCommonPasswordError: invalidFields.some(
        ({ type }) => type === FieldErrorType.CommonPasswordValidationError,
      ),
    },
  };
};

export default parseCreateUserError;
